import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Leaf, Lock, Eye, EyeOff, ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import { COLORS } from '../data/mockData';
import { humanizeApiError } from '../lib/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { setError('Reset link is missing a token. Request a new one.'); return; }
    if (!password || password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setError(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/reset-password`, { token, new_password: password }, { withCredentials: true });
      setSuccess(r.data?.message || 'Password updated. Please sign in.');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      setError(humanizeApiError(err, 'Could not reset password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F9F6F0 0%, #E8ECD7 50%, #F9F6F0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <button onClick={() => navigate('/login')} data-testid="back-to-login"
          style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: COLORS.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <ArrowLeft size={24} color={COLORS.textPrimary} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Leaf size={48} color={COLORS.primary} />
          <h1 style={{ fontSize: 30, fontWeight: 800, color: COLORS.textPrimary, marginTop: 14, fontFamily: 'Outfit, sans-serif' }}>Set New Password</h1>
          <p style={{ fontSize: 15, color: COLORS.textSecondary, marginTop: 6 }}>Choose a strong password (min 6 characters).</p>
        </div>

        {!token && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#FFF8E1', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <AlertCircle size={18} color="#F59E0B" />
            <span style={{ fontSize: 13, color: '#7A4F01' }}>This page requires a reset token. <Link to="/forgot-password" style={{ color: COLORS.primary, fontWeight: 700 }}>Request a new link</Link>.</span>
          </div>
        )}

        {error && (
          <div data-testid="reset-error" style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#FDE8E8', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <AlertCircle size={18} color={COLORS.error} />
            <span style={{ fontSize: 14, color: COLORS.error }}>{error}</span>
          </div>
        )}
        {success && (
          <div data-testid="reset-success" style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#E8F5E9', borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <CheckCircle2 size={18} color="#3CB371" />
            <span style={{ fontSize: 14, color: '#1F5132' }}>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block', marginLeft: 4 }}>New Password</label>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 16px', height: 56, gap: 10 }}>
              <Lock size={20} color={COLORS.textSecondary} />
              <input type={showPassword ? 'text' : 'password'} data-testid="reset-password-input"
                value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters"
                style={{ flex: 1, border: 'none', fontSize: 16, color: COLORS.textPrimary, backgroundColor: 'transparent', outline: 'none', fontFamily: 'Work Sans, sans-serif' }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {showPassword ? <EyeOff size={20} color={COLORS.textSecondary} /> : <Eye size={20} color={COLORS.textSecondary} />}
              </button>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block', marginLeft: 4 }}>Confirm Password</label>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 16px', height: 56, gap: 10 }}>
              <Lock size={20} color={COLORS.textSecondary} />
              <input type={showPassword ? 'text' : 'password'} data-testid="reset-confirm-input"
                value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter password"
                style={{ flex: 1, border: 'none', fontSize: 16, color: COLORS.textPrimary, backgroundColor: 'transparent', outline: 'none', fontFamily: 'Work Sans, sans-serif' }} />
            </div>
          </div>

          <button type="submit" disabled={loading || !token} data-testid="reset-submit-btn"
            style={{ backgroundColor: COLORS.primary, borderRadius: 9999, padding: '16px', border: 'none', cursor: loading ? 'wait' : 'pointer', marginTop: 8, opacity: (loading || !token) ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#FFF' }}>{loading ? 'Updating…' : 'Set New Password'}</span>
          </button>
        </form>
      </div>
    </div>
  );
}
